import indexedDBService from './indexedDBService';
import {productApi} from '~/api';

/**
 * Product Sync Service
 * Handles syncing products between server and IndexedDB
 */
class ProductSyncService
{
    constructor()
    {
        this.isSyncing = false;
        this.syncCallbacks = [];
    }

    /**
     * Register callback for sync events
     */
    onSyncComplete(callback)
    {
        this.syncCallbacks.push(callback);
    }

    /**
     * Trigger sync complete callbacks
     */
    triggerSyncComplete(data)
    {
        this.syncCallbacks.forEach(callback => callback(data));
    }

    /**
     * Check if sync is needed
     */
    async checkSyncNeeded(serverVersion) {
        try {
            await indexedDBService.init();

            const localVersion = await indexedDBService.getMeta('sync_product_version');

            console.log('Sync version check:', {
                localVersion,
                serverVersion,
                needSync: !localVersion || String(localVersion) !== String(serverVersion)
            });

            if (!localVersion) {
                console.log('First sync - no local version found');
                return true; // First sync
            }

            const needSync = String(localVersion) !== String(serverVersion);

            if (needSync) {
                console.log('Sync needed - versions differ');
            }

            return needSync;
        } catch (error) {
            console.error('Error checking sync:', error);
            return true; // Sync on error
        }
    }

    /**
     * Sync products from server
     */
    async syncProducts(forceFullSync = false)
    {
        if (this.isSyncing)
        {
            console.log('Sync already in progress');
            return { success: false, message: 'Sync already in progress' };
        }

        this.isSyncing = true;

        try {
            console.log('📦 Initializing IndexedDB...');
            await indexedDBService.init();

            // Get last sync time
            let lastSyncTime = null;

            if (!forceFullSync)
            {
                lastSyncTime = await indexedDBService.getMeta('sync_product_time');
            }

            console.log('🔄 Starting product sync...', { lastSyncTime, forceFullSync });

            // Call API to get delta changes
            console.log('📡 Calling API...');
            const response = await productApi.sync({
                last_sync_time: lastSyncTime
            });

            console.log('📡 API Response:', response);

            if (response.status !== 'success')
            {
                throw new Error(response.message || 'Sync failed');
            }

            const { products, sync_product_version, sync_time } = response.data;

            console.log(`📊 Received ${products?.length || 0} products from server`);
            console.log('Version:', sync_product_version, 'Time:', sync_time);

            // Process products
            let added = 0;
            let updated = 0;
            let deleted = 0;

            for (const product of products)
            {
                if (product.deleted_at) {
                    // Delete product
                    await indexedDBService.deleteProduct(product.id);
                    deleted++;
                    console.log(`🗑️ Deleted product ID: ${product.id}`);
                }
                else
                {
                    // Add or update product
                    const existing = await indexedDBService.getProduct(product.id);

                    await indexedDBService.saveProduct(product);

                    if (existing)
                    {
                        updated++;
                        console.log(`✏️ Updated product ID: ${product.id} - ${product.title}`);
                    }
                    else
                    {
                        added++;
                        console.log(`➕ Added product ID: ${product.id} - ${product.title}`);
                    }
                }
            }

            // Update meta
            console.log('💾 Updating meta data...');
            await indexedDBService.setMeta('sync_product_version', sync_product_version);

            await indexedDBService.setMeta('sync_product_time', sync_time);

            const count = await indexedDBService.getProductCount();

            const result = {
                success: true,
                added,
                updated,
                deleted,
                total: count,
                version: sync_product_version,
                syncTime: sync_time
            };

            console.log('Sync completed:', result);

            this.triggerSyncComplete(result);

            return result;
        }
        catch (error)
        {
            console.error('Sync error:', error);

            return {
                success: false,
                message: error.message || 'Sync failed'
            };
        }
        finally
        {
            this.isSyncing = false;
        }
    }

    /**
     * Force full sync (clear and re-sync all)
     */
    async forceFullSync()
    {
        try
        {
            await indexedDBService.init();
            await indexedDBService.clearProducts();
            await indexedDBService.setMeta('sync_product_time', null);

            return await this.syncProducts(true);
        }
        catch (error)
        {
            console.error('Force full sync error:', error);
            return {
                success: false,
                message: error.message || 'Force sync failed'
            };
        }
    }

    /**
     * Search products locally
     */
    async searchProducts(keyword, limit = 50) {
        try {
            await indexedDBService.init();
            return await indexedDBService.searchProducts(keyword, limit);
        } catch (error) {
            console.error('Search error:', error);
            return [];
        }
    }

    /**
     * Get all products
     */
    async getAllProducts() {
        try {
            await indexedDBService.init();
            return await indexedDBService.getAllProducts();
        } catch (error) {
            console.error('Get all products error:', error);
            return [];
        }
    }

    /**
     * Get product by ID
     */
    async getProduct(id) {
        try {
            await indexedDBService.init();
            return await indexedDBService.getProduct(id);
        } catch (error) {
            console.error('Get product error:', error);
            return null;
        }
    }

    /**
     * Get sync status
     */
    async getSyncStatus() {
        try {
            await indexedDBService.init();

            const version = await indexedDBService.getMeta('sync_product_version');
            const syncTime = await indexedDBService.getMeta('sync_product_time');
            const count = await indexedDBService.getProductCount();

            return {
                version,
                syncTime,
                count,
                isSyncing: this.isSyncing
            };
        } catch (error) {
            console.error('Get sync status error:', error);
            return null;
        }
    }
}

// Export singleton instance
const productSyncService = new ProductSyncService();

export default productSyncService;
